import React from 'react';
import { Play, Calendar, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { artistData, socialLinks } from '../data/mock';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_alexander-richard/artifacts/zikr6fy1_IMG_0345.jpeg"
          alt="Alexander Richard performing"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/90 to-stone-950/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-stone-950/50" />
      </div>
      
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-gold-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-400/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-gold-500/20 border border-gold-500/30 rounded-full px-4 py-2 mb-6">
            <MapPin className="w-4 h-4 text-gold-400" />
            <span className="text-gold-300 text-sm font-medium">Based in Omaha, Nebraska</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
            <span className="block">Alexander</span>
            <span className="block text-gold-400">Richard</span>
          </h1>

          {/* Business Name */}
          <p className="text-xl sm:text-2xl text-stone-400 font-light mb-6 tracking-wide">
            {artistData.businessName}
          </p>

          {/* Tagline */}
          <p className="text-lg sm:text-xl text-stone-300 mb-8 max-w-xl leading-relaxed">
            {artistData.tagline} — Bringing nostalgia-driven country music and classic rock from the 60's to early 2000's to stages across the Midwest.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-12">
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-gold-500 hover:bg-gold-600 text-white px-8 py-6 text-lg font-semibold rounded-md transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/30 hover:-translate-y-0.5"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book Your Event
            </Button>
            <a
              href={socialLinks.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-6 text-lg font-medium rounded-md transition-all duration-300 border border-white/20 hover:border-white/40"
            >
              <Play className="w-5 h-5" />
              Watch Live
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8 pt-8 border-t border-stone-700/50">
            <div>
              <p className="text-3xl font-bold text-gold-400">5+</p>
              <p className="text-stone-400 text-sm">Years Touring</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gold-400">12-16</p>
              <p className="text-stone-400 text-sm">Shows Monthly</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gold-400">8</p>
              <p className="text-stone-400 text-sm">States Covered</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-gold-400 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
